import { useCallback, useEffect, useState } from 'react';
import type { AllianceGoalKind, AllianceOverview } from '@galaxis/shared';
import { ALLIANCE_GOAL_KINDS, ALLIANCE_GOAL_LABELS } from '@galaxis/shared';
import {
  fetchAlliance, createAlliance, inviteToAlliance, respondAllianceInvite, leaveAlliance, createAllianceGoal, saveAllianceWebhook,
} from '../../api/social.ts';
import { fetchDiplomacyOverview } from '../../api/galaxy.ts';
import { useGameStore } from '../../store/game-store.ts';

export function AlliancePanel() {
  const [data, setData] = useState<AllianceOverview | null>(null);
  const [known, setKnown] = useState<Array<{ civId: string; civName: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [invitee, setInvitee] = useState('');
  const [goalKind, setGoalKind] = useState<AllianceGoalKind>('systems');
  const [goalTarget, setGoalTarget] = useState(3);
  const [webhook, setWebhook] = useState('');
  const pushToast = useGameStore(s => s.pushToast);

  const load = useCallback(() => {
    fetchAlliance().then(setData).catch(e => pushToast(e instanceof Error ? e.message : 'Hiba', 'error'));
    fetchDiplomacyOverview().then(o => setKnown(o.relations.filter(r => !r.isNpc).map(r => ({ civId: r.civId, civName: r.civName })))).catch(() => undefined);
  }, [pushToast]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); pushToast(ok); load(); } catch (e) { pushToast(e instanceof Error ? e.message : 'A kérés nem sikerült', 'error'); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-slate-500">Szövetség betöltése…</p>;
  const a = data.alliance;

  return (
    <div className="space-y-5" data-testid="alliance-panel">
      {data.incomingInvites.length > 0 && (
        <section aria-label="Meghívók" className="sci-fi-panel rounded-xl p-4">
          <h2 className="text-sm font-semibold text-slate-200 mb-2">Szövetségi meghívók</h2>
          <ul className="space-y-2">
            {data.incomingInvites.map(i => (
              <li key={i.id} className="flex items-center justify-between gap-2 text-sm text-slate-300">
                <span>[{i.allianceTag}] {i.allianceName} <span className="text-xs text-slate-500">– {i.invitedBy}</span></span>
                <span className="flex gap-2">
                  <button type="button" className="btn-primary text-xs" disabled={busy || !!a} onClick={() => void act(() => respondAllianceInvite(i.id, true), 'Csatlakoztál a szövetséghez')}>Elfogad</button>
                  <button type="button" className="nav-btn text-xs" disabled={busy} onClick={() => void act(() => respondAllianceInvite(i.id, false), 'Meghívó elutasítva')}>Elutasít</button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!a ? (
        <section className="sci-fi-panel rounded-xl p-4" aria-label="Szövetség alapítása">
          <h2 className="text-sm font-semibold text-slate-200">Nem vagy szövetség tagja</h2>
          <p className="text-xs text-slate-400 mt-1">Alapíts egyet, vagy várj meghívásra. A szövetség közös célokat tűz ki, és közös értesítéseket kap.</p>
          <form className="mt-3 grid sm:grid-cols-[1fr_120px_auto] gap-2 items-end" onSubmit={e => { e.preventDefault(); void act(() => createAlliance(name.trim(), tag.trim()), 'Szövetség alapítva'); }}>
            <label className="text-xs text-slate-400">Név
              <input className="mt-1 w-full bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100" value={name} maxLength={32} onChange={e => setName(e.target.value)} />
            </label>
            <label className="text-xs text-slate-400">Rövidítés
              <input className="mt-1 w-full bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100 uppercase" value={tag} maxLength={5} onChange={e => setTag(e.target.value)} />
            </label>
            <button type="submit" className="btn-primary min-h-[44px]" disabled={busy || name.trim().length < 3 || tag.trim().length < 2}>Alapítás</button>
          </form>
        </section>
      ) : (
        <>
          <section className="sci-fi-panel rounded-xl p-4" aria-label="Szövetség">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-100" data-testid="alliance-name">[{a.tag}] {a.name}</h2>
              <span className="text-xs text-slate-400">Rangod: {a.role === 'leader' ? 'vezető' : a.role === 'officer' ? 'tiszt' : 'tag'}</span>
            </div>
            <ul className="mt-3 space-y-1" data-testid="alliance-members">
              {a.members.map(m => (
                <li key={m.civId} className="flex justify-between text-sm text-slate-300">
                  <span>{m.name} <span className="text-[11px] text-slate-500">{m.role === 'leader' ? 'vezető' : m.role === 'officer' ? 'tiszt' : ''}</span></span>
                  <span className="font-mono text-accent">{m.score}</span>
                </li>
              ))}
            </ul>
            {a.role !== 'member' && (
              <form className="mt-3 flex flex-wrap gap-2 items-end" onSubmit={e => { e.preventDefault(); void act(() => inviteToAlliance(invitee), 'Meghívó elküldve'); }}>
                <label className="text-xs text-slate-400">Meghívás
                  <select className="mt-1 block bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100 min-w-[180px]" value={invitee} onChange={e => setInvitee(e.target.value)} aria-label="Meghívandó civilizáció">
                    <option value="">Válassz civilizációt…</option>
                    {known.map(k => <option key={k.civId} value={k.civId}>{k.civName}</option>)}
                  </select>
                </label>
                <button type="submit" className="btn-primary min-h-[44px]" disabled={busy || !invitee}>Meghív</button>
              </form>
            )}
            {a.invites.length > 0 && <p className="text-xs text-slate-500 mt-2">Függő meghívó: {a.invites.map(i => i.targetName).join(', ')}</p>}
            {a.role !== 'leader' && (
              <button type="button" className="nav-btn text-xs mt-3" disabled={busy} onClick={() => void act(leaveAlliance, 'Kiléptél a szövetségből')}>Kilépés</button>
            )}
          </section>

          <section className="sci-fi-panel rounded-xl p-4" aria-label="Közös célok" data-testid="alliance-goals">
            <h2 className="text-sm font-semibold text-slate-200 mb-2">Közös célok</h2>
            {a.goals.length === 0 ? <p className="text-xs text-slate-500">Még nincs kitűzött cél.</p> : (
              <ul className="space-y-3">
                {a.goals.map(g => (
                  <li key={g.id} data-status={g.status}>
                    <div className="flex justify-between text-sm text-slate-300">
                      <span>{g.label}: {g.target}</span>
                      <span className="font-mono text-xs">{g.status === 'completed' ? '✓ teljesítve' : g.status === 'expired' ? 'lejárt' : `${g.progress} / ${g.target}`}</span>
                    </div>
                    <div className="mt-1 h-2 rounded bg-slate-800 overflow-hidden">
                      <div className={`h-full ${g.status === 'completed' ? 'bg-emerald-500' : 'bg-accent'}`} style={{ width: `${Math.min(100, (100 * g.progress) / g.target)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {a.role !== 'member' && (
              <form className="mt-3 flex flex-wrap gap-2 items-end" onSubmit={e => { e.preventDefault(); void act(() => createAllianceGoal(goalKind, goalTarget), 'Cél kitűzve'); }}>
                <label className="text-xs text-slate-400">Cél
                  <select className="mt-1 block bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100" value={goalKind} onChange={e => setGoalKind(e.target.value as AllianceGoalKind)} aria-label="Cél típusa">
                    {ALLIANCE_GOAL_KINDS.map(k => <option key={k} value={k}>{ALLIANCE_GOAL_LABELS[k]}</option>)}
                  </select>
                </label>
                <label className="text-xs text-slate-400">Mennyiség
                  <input type="number" min={1} max={500} className="mt-1 block w-24 bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100" value={goalTarget} onChange={e => setGoalTarget(Math.max(1, Number(e.target.value) || 1))} aria-label="Cél mennyisége" />
                </label>
                <button type="submit" className="btn-primary min-h-[44px]" disabled={busy}>Cél kitűzése</button>
              </form>
            )}
          </section>

          {a.role === 'leader' && (
            <section className="sci-fi-panel rounded-xl p-4" aria-label="Discord">
              <h2 className="text-sm font-semibold text-slate-200">Szövetségi Discord-értesítés</h2>
              <p className="text-xs text-slate-400 mt-1">Egy Discord-webhook, amelyre a szövetségi hírek kerülnek ({a.discordConfigured ? (a.discordEnabled ? 'be van állítva és be van kapcsolva' : 'be van állítva, ki van kapcsolva') : 'nincs beállítva'}). A cím titok: beállítás után nem látszik újra.</p>
              <form className="mt-2 flex flex-wrap gap-2" onSubmit={e => { e.preventDefault(); void act(() => saveAllianceWebhook(webhook.trim()), webhook.trim() ? 'Webhook mentve' : 'Webhook törölve').then(() => setWebhook('')); }}>
                <input className="flex-1 min-w-[200px] bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100 text-sm" placeholder="https://discord.com/api/webhooks/…" value={webhook} onChange={e => setWebhook(e.target.value)} aria-label="Discord webhook cím" autoComplete="off" />
                <button type="submit" className="btn-primary min-h-[44px]" disabled={busy}>{webhook.trim() ? 'Mentés' : 'Törlés'}</button>
              </form>
            </section>
          )}
        </>
      )}
    </div>
  );
}
